import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import '../styles/Auth.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) setError('Missing reset token. Please request a new password reset link.');
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        const msg = Array.isArray(data.detail) ? data.detail.map(d => d.msg).join(', ') : (data.detail || 'Reset failed.');
        throw new Error(msg);
      }
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-background">
        <video className="auth-video" autoPlay loop muted playsInline>
          <source src="/hero_video.mp4" type="video/mp4" />
        </video>
        <div className="auth-video-overlay"></div>
      </div>

      <div className="auth-container">
        <div className="auth-card" style={{ maxWidth: 480 }}>
          <div className="auth-header">
            <Link to="/" className="auth-logo">
              <img
                src="https://customer-assets.emergentagent.com/job_5fec953c-1553-4442-b50d-48094837ff2f/artifacts/yv8qmlbd_AI_Architecture_Logo__2_-removebg-preview.png"
                alt="Ezai Build"
                className="logo-icon"
                style={{ width: 44, height: 44 }}
              />
              <span className="logo-text">Ezai Build</span>
            </Link>
            <h1 className="display-medium auth-title">Reset password</h1>
            <p className="body-medium auth-subtitle">
              {done ? "Password updated! Redirecting…" : "Choose a new password for your account."}
            </p>
          </div>

          {done ? (
            <div data-testid="reset-done-state" style={{ textAlign: 'center', padding: '16px 0' }}>
              <CheckCircle2 size={48} color="var(--brand-primary)" style={{ marginBottom: 16 }} />
              <p className="body-medium">Password updated successfully.</p>
              <p className="body-small" style={{ color: 'var(--text-muted)', marginTop: 8 }}>
                Redirecting you to sign in…
              </p>
            </div>
          ) : !token ? (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <XCircle size={48} color="#ff6b6b" style={{ marginBottom: 16 }} />
              <p className="body-medium">{error}</p>
              <Link to="/forgot-password" className="auth-link" style={{ display: 'inline-block', marginTop: 16 }}>
                Request new link
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="auth-form">
              <div className="form-group">
                <label className="form-label body-medium">New password</label>
                <div className="input-wrapper">
                  <Lock size={20} className="input-icon" />
                  <input
                    type={show ? 'text' : 'password'}
                    className="form-input"
                    placeholder="At least 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    data-testid="reset-password-input"
                  />
                  <button type="button" className="toggle-password" onClick={() => setShow(!show)}>
                    {show ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label body-medium">Confirm password</label>
                <div className="input-wrapper">
                  <Lock size={20} className="input-icon" />
                  <input
                    type={show ? 'text' : 'password'}
                    className="form-input"
                    placeholder="Re-enter your password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    minLength={8}
                    data-testid="reset-confirm-input"
                  />
                </div>
              </div>

              {error && <p className="auth-error body-small" data-testid="auth-error">{error}</p>}

              <Button
                type="submit"
                className="btn-primary btn-large auth-btn"
                disabled={submitting}
                data-testid="reset-submit-btn"
              >
                {submitting ? <Loader2 size={20} className="spinner" /> : 'Update password'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
